import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { printService } from 'api/services/printService';
import type { PrintJob } from 'api/types';
import { JobStatusChip, fmtTime, apiMessage } from 'components/modules/print-module/printUi';

/** One print job and its event history (claimed, sent, failed, retried, rerouted, …). A view dialog. */
interface Props {
  jobId: string | null;
  onClose: () => void;
}

const EVENT_LABEL: Record<string, string> = {
  QUEUED: 'Queued', CLAIMED: 'Claimed by agent', LEASE_EXPIRED: 'Claim expired', SENT: 'Sent to printer', PRINTED: 'Printed',
  RETRY_SCHEDULED: 'Attempt failed, will retry', FAILED: 'Failed', REROUTED: 'Rerouted', CANCELLED: 'Cancelled',
  RETRIED: 'Retried by staff', RESOLVED: 'Marked handled',
};

const PrintJobDetailModal: React.FC<Props> = ({ jobId, onClose }) => {
  const [job, setJob] = useState<PrintJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    setJob(null); setError(null);
    printService.getJob(jobId)
      .then((j) => { if (alive) setJob(j); })
      .catch((err) => { if (alive) setError(apiMessage(err, 'The print job could not be loaded.')); });
    return () => { alive = false; };
  }, [jobId]);

  if (!jobId) return null;
  const events = [...(job?.events ?? [])].sort((a, b) => String(a.occurredAt).localeCompare(String(b.occurredAt)));

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 820 }} data-job-detail>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Print job{job ? ` — ${job.sourceReference ?? job.documentType}` : ''}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {error && <div className="form-error-message">{error}</div>}
          {!error && !job && <p style={{ margin: 0 }}>Loading…</p>}
          {job && (
            <>
              <dl className="prn-kv">
                <dt>Status</dt><dd><JobStatusChip job={job} /></dd>
                <dt>Ticket</dt><dd>{job.sourceReference ?? '—'}{job.sourceLabel ? ` · ${job.sourceLabel}` : ''}</dd>
                <dt>Printer</dt><dd>{job.printerName ?? '—'}{job.originalPrinterName ? ` (from ${job.originalPrinterName})` : ''}</dd>
                <dt>Agent</dt><dd>{job.agentName ?? '—'}{job.agentName ? (job.agentOnline ? ' · online' : ' · offline') : ''}</dd>
                <dt>Copy</dt><dd>
                  {job.copyKind === 'REPRINT' ? `Reprint ${job.reprintNumber ?? ''}` : job.copyKind === 'REROUTED' ? 'Rerouted copy' : `Copy ${job.copyNumber} of ${job.copyCount}`}
                  {job.possibleDuplicate ? ' · printed DUPLICATE' : ''}
                </dd>
                <dt>Attempts</dt><dd>{job.attempts}{job.failedAttempts ? ` (${job.failedAttempts} failed)` : ''}</dd>
                <dt>Requested</dt><dd>{fmtTime(job.createdAt)}{job.requestedByLabel ? ` by ${job.requestedByLabel}` : ''}{job.requestReason ? ` — ${job.requestReason}` : ''}</dd>
                {job.lastError && <><dt>Last error</dt><dd style={{ color: 'var(--danger, #dc2626)' }}>{job.lastErrorCode ? `${job.lastErrorCode}: ` : ''}{job.lastError}</dd></>}
                {job.cancelledAt && <><dt>Cancelled</dt><dd>{fmtTime(job.cancelledAt)}{job.cancelReason ? ` — ${job.cancelReason}` : ''}</dd></>}
                {job.resolvedAt && <><dt>Handled</dt><dd>{fmtTime(job.resolvedAt)}{job.resolvedByName ? ` by ${job.resolvedByName}` : ''}{job.resolutionNote ? ` — ${job.resolutionNote}` : ''}</dd></>}
              </dl>
              <h3 style={{ fontSize: '0.9rem', margin: '8px 0' }}>History</h3>
              {events.length === 0 ? <p style={{ color: 'var(--text-muted)', margin: 0 }}>No events recorded.</p> : (
                <table className="prn-events" data-job-events>
                  <thead><tr><th>When</th><th>What</th><th>Printer / agent</th><th>Detail</th><th>By</th></tr></thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(e.occurredAt)}</td>
                        <td>{EVENT_LABEL[e.eventType] ?? e.eventType}{e.attempt ? ` (#${e.attempt})` : ''}</td>
                        <td>{[e.printerName, e.agentName].filter(Boolean).join(' · ') || '—'}</td>
                        <td>{[e.errorCode, e.detail].filter(Boolean).join(': ') || '—'}</td>
                        <td>{e.byName ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default PrintJobDetailModal;
